/**
 * Localization Utility for Sentlo Quick Action Buttons
 * Supports Nigerian languages (Yoruba, Igbo, Hausa) and other regions.
 */

export type LanguageCode = 'en' | 'yo' | 'ig' | 'ha' | 'fr' | 'gh'; // 'gh' as shorthand for Ghanaian English/Pidgin if needed

export interface QuickActionTranslation {
  heavyGoSlow: string;
  lightIsOut: string;
  reportNow: string;
  shareUpdate: string;
  heavyRain: string;
  securityPresence: string;
  helpNeeded: string;
  marketPriceUpdate: string;
}

export const QUICK_ACTIONS: Record<LanguageCode, QuickActionTranslation> = {
  en: {
    heavyGoSlow: 'Heavy Go-Slow Here',
    lightIsOut: 'Light is Out Here',
    reportNow: 'Report Now!',
    shareUpdate: 'SHARE UPDATE',
    heavyRain: 'Heavy Rain Here',
    securityPresence: 'Security Check Here',
    helpNeeded: 'I Need Help!',
    marketPriceUpdate: 'Price Check Here'
  },
  yo: { // Yoruba
    heavyGoSlow: 'Go-Slow pọ̀ nibí',
    lightIsOut: 'Iná ti lọ nibí',
    reportNow: 'Sọ Ohun To Nṣẹlẹ̀!',
    shareUpdate: 'PIN IWIFUN',
    heavyRain: 'Òjò pọ̀ nibí',
    securityPresence: 'Awọn Agbofinro wa nibí',
    helpNeeded: 'Mo fẹ́ ìrànlọ́wọ́!',
    marketPriceUpdate: 'Iye owó Ọjà nibí'
  },
  ig: { // Igbo
    heavyGoSlow: 'Go-Slow dị ebe a',
    lightIsOut: 'Ọkụ agpala ebe a',
    reportNow: 'Kọọ akụkọ ugbu a!',
    shareUpdate: 'KESAA AKỤKỌ',
    heavyRain: 'Mmiri na-ezonụ ebe a',
    securityPresence: 'Ndị uwe ojii nọ ebe a',
    helpNeeded: 'Achọrọ m enyemaka!',
    marketPriceUpdate: 'Ọnụ ahịa ahịa ebe a'
  },
  ha: { // Hausa
    heavyGoSlow: 'Go-Slow da yawa a nan',
    lightIsOut: 'Babu wutar lantarki a nan',
    reportNow: 'Ba da rahoton yanzu!',
    shareUpdate: 'RABA LABARI',
    heavyRain: 'Ruwan sama da yawa a nan',
    securityPresence: 'Akwai jami’an tsaro a nan',
    helpNeeded: 'Ina bukatar taimako!',
    marketPriceUpdate: 'Farashin kaya a nan'
  },
  fr: { // French (for future global expansion/bordering countries)
    heavyGoSlow: 'Gros Embouteillage Ici',
    lightIsOut: 'Coupure d’Électricité Ici',
    reportNow: 'Signaler Maintenant!',
    shareUpdate: 'PARTAGER L’ACTUALITÉ',
    heavyRain: 'Forte Pluie Ici',
    securityPresence: 'Présence de Sécurité Ici',
    helpNeeded: 'J’ai besoin d’aide!',
    marketPriceUpdate: 'Mise à Jour des Prix'
  },
  gh: { // Ghana (Pidgin/English)
    heavyGoSlow: 'Traffic go-slow for here',
    lightIsOut: 'Light off for here',
    reportNow: 'Report Now!',
    shareUpdate: 'SHARE UPDATE',
    heavyRain: 'Heavy rain for here',
    securityPresence: 'Security dey here',
    helpNeeded: 'I need help!',
    marketPriceUpdate: 'Market price for here'
  }
};

/**
 * Gets the preferred language based on country and browser setting
 * Prioritizes manually set user language override
 */
export function getPreferredLanguage(country: string): LanguageCode {
  // 1. Check for manual override in localStorage
  const manualOverride = localStorage.getItem('sentlo_manual_lang');
  if (manualOverride && Object.keys(QUICK_ACTIONS).includes(manualOverride)) {
    return manualOverride as LanguageCode;
  }

  // 2. Check for general language preference
  const saved = localStorage.getItem('sentlo_lang');
  if (saved && Object.keys(QUICK_ACTIONS).includes(saved)) return saved as LanguageCode;

  // 3. Fallback to region-based defaults
  if (country === 'Nigeria') {
    return 'en';
  }
  
  if (country === 'Ghana') return 'gh';
  
  const browserLang = navigator.language.split('-')[0] as LanguageCode;
  if (Object.keys(QUICK_ACTIONS).includes(browserLang)) return browserLang;
  
  return 'en';
}

/**
 * Gets a translated quick action string
 */
export function getQuickActionLabel(action: keyof QuickActionTranslation, lang: LanguageCode): string {
  return QUICK_ACTIONS[lang]?.[action] || QUICK_ACTIONS['en'][action];
}
